import { initializeApp } from 'firebase/app'
import { getAuth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, GoogleAuthProvider } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'

// authDomain on www.vinarai.com (the app's own origin) — final state
// after user completed both manual registrations:
//
//   - Google Cloud Console → Credentials → OAuth 2.0 Client →
//     Authorized Redirect URIs includes
//     https://www.vinarai.com/__/auth/handler
//   - Apple Developer → Services ID → Web Authentication Config →
//     Domains and Subdomains includes www.vinarai.com,
//     Return URLs includes https://www.vinarai.com/__/auth/handler
//
// Why same-origin matters: signInWithRedirect (mobile) stores the
// resolved credential in localStorage on whichever origin handled
// the OAuth callback. If that origin differs from the app origin,
// the credential is unreachable when the user lands back on the
// app → sign-in loops. With authDomain on www.vinarai.com, the
// OAuth callback runs on www.vinarai.com (proxied to Firebase's
// hosted handler at wine-cellar-69da4.firebaseapp.com via the
// /__/auth/* + /__/firebase/* Vercel rewrites in vercel.json), so
// credentials land on the same origin the app reads from.
//
// Bonus: the OAuth consent screen now reads "vinarai.com" directly
// instead of the auth.vinarai.com subdomain.
//
// NATIVE OVERRIDE (2026-05-24): on Capacitor (iOS/Android), the app
// runs at the capacitor://localhost scheme. Firebase Auth SDK loads
// an iframe at https://{authDomain}/__/auth/iframe.js on init to
// broker auth state — that cross-origin load hangs forever from a
// capacitor:// parent (WKWebView cross-scheme + Vercel rewrite
// chain breaks the iframe handshake). The spinner stays on the
// splash forever because onAuthStateChanged never fires. Pointing
// authDomain directly at the firebaseapp.com host on native sidesteps
// the rewrite chain entirely — Firebase serves the iframe directly
// from there with no proxy, the handshake completes, and Auth init
// can finish. Native sign-in flows don't use this iframe anyway
// (they go through @capacitor-firebase/authentication +
// signInWithCredential), so the authDomain value here only matters
// for Auth SDK init, not for any OAuth UI.
const _isCapacitor = typeof window !== 'undefined'
  && typeof window.Capacitor?.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform()
const firebaseConfig = {
  apiKey: "AIzaSyCo6dV2Mk7y3zUwmIEmlv94KLrGEI4_hsQ",
  authDomain: _isCapacitor
    ? "wine-cellar-69da4.firebaseapp.com"
    : "www.vinarai.com",
  projectId: "wine-cellar-69da4",
  storageBucket: "wine-cellar-69da4.firebasestorage.app",
  messagingSenderId: "638973153478",
  appId: "1:638973153478:web:831048057634424d77c7c7"
}

const app = initializeApp(firebaseConfig)

// On Capacitor (iOS/Android), initialize Auth with explicit
// indexedDBLocalPersistence. The default getAuth() triggers an
// auto-persistence-detection sequence that probes window.indexedDB
// via a synchronous handshake — that handshake can stall on WKWebView's
// first launch (the IndexedDB worker isn't ready synchronously like in
// regular Safari). Explicit init bypasses the probe and uses IndexedDB
// directly, which IS supported in WKWebView, just not detectable via
// the SDK's auto-probe.
//
// On web, getAuth() works fine — auto-detection finds browserLocal
// (localStorage) without any handshake stall.
export const auth = _isCapacitor
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app)
export const provider = new GoogleAuthProvider()
export const db = getFirestore(app)

// Storage SDK is ~80 KB of the vendor-firebase chunk and only used on
// upload paths (AddWine label, BulkUpload, EditWine, LiveTasting
// uncork photo, Merchant logo). Lazy-loaded so consumer users who
// only browse their cellar never pay for it.
//
// Returns { storage, ref, uploadBytes, getDownloadURL } so callers
// can destructure everything in one await. Pre-fix each consumer
// page did:
//   import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
//   import { storage } from '../firebase'
// Both of those caused firebase/storage to land in the eager
// vendor-firebase chunk. Now everything goes through this lazy
// accessor + the storage chunk only loads on actual upload.
let _storage = null
let _storageBundle = null
export async function getStorageLazy() {
  if (_storageBundle) return _storageBundle
  const { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, listAll } = await import('firebase/storage')
  if (!_storage) _storage = getStorage(app)
  _storageBundle = { storage: _storage, ref, uploadBytes, getDownloadURL, deleteObject, listAll }
  return _storageBundle
}

// Functions SDK is heavy and only used by getWineImage. Lazy-loaded via
// `getCloudFunctions()` below — first call dynamically imports the SDK and
// initializes the instance, subsequent calls return the cached instance.
let _functions = null
export async function getCloudFunctions() {
  if (_functions) return _functions
  const { getFunctions } = await import('firebase/functions')
  _functions = getFunctions(app)
  return _functions
}